/**
 * FORMATTING HELPERS
 *
 * Every function here is PURE: same input always gives the same output, and
 * nothing outside the function is touched. Pure functions are trivial to test
 * and safe to call from anywhere — server, browser, or a script.
 *
 * These live in `shared` because the customer app, the admin app AND the
 * server all need to render "₹1,249" identically. One implementation means
 * one place to fix a rounding bug.
 */

/**
 * Formats a number as Indian Rupees using the Indian digit grouping system
 * (1,00,000 — lakhs and crores — not the Western 100,000).
 *
 * @param {number} amount
 * @param {{ showDecimals?: boolean }} [options]
 * @returns {string} e.g. "₹1,249"
 */
export function formatCurrency(amount, options = {}) {
  const { showDecimals = false } = options;
  const safeAmount = Number.isFinite(amount) ? amount : 0;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(safeAmount);
}

/**
 * Converts a product name into a URL-safe slug.
 *   "Maggi 2-Minute Noodles (70g)" -> "maggi-2-minute-noodles-70g"
 *
 * WHY SLUGS: `/product/maggi-noodles` is readable, shareable, and ranks in
 * Google. `/product/507f1f77bcf86cd799439011` is none of those things.
 *
 * @param {string} text
 * @returns {string}
 */
export function slugify(text) {
  if (typeof text !== 'string') return '';

  return text
    .toLowerCase()
    .trim()
    .normalize('NFD') // split accented letters into letter + accent
    .replace(/[\u0300-\u036f]/g, '') // strip the accent marks
    .replace(/[^a-z0-9\s-]/g, '') // drop anything not a letter/number/space/dash
    .replace(/\s+/g, '-') // spaces become dashes
    .replace(/-+/g, '-') // collapse repeated dashes
    .replace(/^-|-$/g, ''); // trim dashes from the ends
}

/**
 * Percentage saved when a discount price is present.
 *
 * @param {number} price - original price
 * @param {number} [discountPrice] - the reduced price
 * @returns {number} whole-number percentage; 0 when there is no valid discount
 */
export function calculateDiscountPercent(price, discountPrice) {
  if (!Number.isFinite(price) || !Number.isFinite(discountPrice)) return 0;
  if (price <= 0 || discountPrice <= 0 || discountPrice >= price) return 0;

  return Math.round(((price - discountPrice) / price) * 100);
}

/**
 * The price a customer actually pays: the discount price when one is set and
 * valid, otherwise the original price.
 *
 * ⚠️ The SERVER must always call this when building an order. Never trust a
 * price sent by the browser — anyone can edit it in DevTools.
 *
 * @param {{ price: number, discountPrice?: number }} product
 * @returns {number}
 */
export function getEffectivePrice(product) {
  if (!product || !Number.isFinite(product.price)) return 0;

  const hasValidDiscount =
    Number.isFinite(product.discountPrice) &&
    product.discountPrice > 0 &&
    product.discountPrice < product.price;

  return hasValidDiscount ? product.discountPrice : product.price;
}

/**
 * Truncates text and appends an ellipsis, without cutting a word in half.
 *
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
export function truncate(text, maxLength = 60) {
  if (typeof text !== 'string' || text.length <= maxLength) return text ?? '';

  const clipped = text.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(' ');

  return `${lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped}…`;
}

/**
 * Compact relative time: "just now", "5m ago", "3h ago", "2d ago".
 * Used in the admin panel where "2m ago" is far more useful than a timestamp.
 *
 * @param {string|Date} date
 * @returns {string}
 */
export function formatRelativeTime(date) {
  const then = new Date(date);
  if (Number.isNaN(then.getTime())) return '';

  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
